import { createSelector } from 'reselect';

import { PAYER_INTEGRATIONS } from 'constants/IntegrationConstants';
import { getSelectedGroupId, getSelectedGroupIntegrations } from 'modules/groups/selectors';
import { filterRequestFormatter } from 'modules/interventions/utils';
import { isGroupManagement } from 'modules/router/selectors';
import { getSelectedDealerId, getSelectedDealerIntegrations } from 'selectors/dealerSelectors';

/** @returns {import("../reducer").PayerReducer} */
const getState = state => state.payers;

export const getRoot = root => root;

export const getProps = (_, props) => props;

const getDataState = createSelector(getState, state => state.data);

export const getUIState = createSelector(getState, state => state.ui);

const getPayersEntries = createSelector(getDataState, data => data.entities);

export const getPayers = createSelector(getPayersEntries, entries => Object.values(entries));

export const getPayerById = createSelector(getPayersEntries, getProps, (entries, payerId) => entries[payerId]);

export const getPayersByIds = createSelector(
  getPayersEntries,
  getProps,
  (payers, payerIds = []) => payerIds.reduce((acc, payerId) => {
    if (payers[payerId]) {
      acc.push(payers[payerId]);
    }
    return acc;
  }, [])
);

const getUIDashboard = createSelector(getUIState, ui => ui.dashboard);

const getUIDashboardData = createSelector(getUIDashboard, dashboard => dashboard.data);

const getDashboardIds = createSelector(getUIDashboardData, data => data.ids);

export const getDashboardPage = createSelector(getUIDashboardData, data => data.page);

export const isDashboardLoading = createSelector(getUIDashboardData, data => data.loading);

export const getDashboardEntries = createSelector(getRoot, getDashboardIds, getPayersByIds);

const getFilters = createSelector(getUIDashboard, dashboard => dashboard.filters);

export const getBaseRequestParams = createSelector(
  getDashboardPage,
  getFilters,
  getSelectedGroupId,
  getSelectedDealerId,
  isGroupManagement,
  ({ size, number: page }, filters, groupId, dealerId, isGroup) => ({
    size,
    page,
    projection: 'page',
    ...isGroup ? { groupId } : { dealerId },
    sort: 'modifiedOn,desc',
    ...filterRequestFormatter(filters),
  })
);

export const getDeletingItems = createSelector(getUIDashboard, dashboard => dashboard.deleting);

export const deletingDashboardItem = createSelector(
  getDeletingItems,
  getProps,
  (deleting, payerId) => deleting.includes(payerId)
);

const getUIDetails = createSelector(getUIState, ui => ui.details);

const getLoadingDetailsState = createSelector(getUIDetails, ui => ui.loading);

const creating = createSelector(getLoadingDetailsState, loading => loading.create);

const updating = createSelector(getLoadingDetailsState, loading => loading.update);

export const ready = createSelector(getUIDetails, ui => ui.ready);

export const fetching = createSelector(getLoadingDetailsState, loading => loading.fetch);

export const saving = createSelector(creating, updating, (create, update) => create || update);

export const getIntegrations = createSelector(
  getSelectedGroupIntegrations,
  getSelectedDealerIntegrations,
  getProps,
  (groupIntegrations, dealerIntegrations, isGroup) => {
    const integrations = (isGroup ? groupIntegrations : dealerIntegrations);
    return PAYER_INTEGRATIONS.filter(integration => integrations.includes(integration));
  }
);
